import { useState, useEffect } from "react";
import { IconKey } from "@tabler/icons-react";
import { getAppState, useAppState } from "../state/useAppState";
import { validateApiKeyAsync } from "../api";
import { useNavigate } from "react-router-dom";

let isThereApiKey = false;

function showPopup(setShowApiKeyPopup: (value: boolean) => void) {
    const apiKey = getAppState().apiKey;

    if (!apiKey) {
        setShowApiKeyPopup(true);
    }
}

const findApiKeyParam = (): string | null => {
    const params: URLSearchParams = new URLSearchParams(window.location.search);

    for (const [key, value] of params.entries()) {
        if (key.toLowerCase() === "apikey" || key.toLowerCase() === "api_key") {
            return value;
        }
    }

    return null;
};

export const isThereApiKeyParam = (): boolean => {
    isThereApiKey = isThereApiKey || findApiKeyParam() !== null;
    return isThereApiKey;
};

type UseApiKeyActionOptions = {
    callback?: () => void | null;
    enabled?: boolean;
};

export const useApiKeyAction = ({ callback, enabled = true }: UseApiKeyActionOptions = {}) => {
    const navigate = useNavigate();
    const [showApiKeyPopup, setShowApiKeyPopup] = useState(false);
    const setApiKeyAndApplication = useAppState(state => state.setApiKeyAndApplication);

    // Check for API key on mount and show popup if missing
    useEffect(() => {
        if (!enabled) {
            if (callback) {
                callback();
            }
            return;
        }

        const apiKeyParam = findApiKeyParam();

        if (apiKeyParam) {
            // Validate API key and get application information
            validateApiKeyAsync(apiKeyParam)
                .then(applicationInfo => {
                    // Store both API key and application info
                    setApiKeyAndApplication(apiKeyParam, applicationInfo);
                })
                .catch(() => {
                    showPopup(setShowApiKeyPopup);
                })
                .finally(() => {
                    if (callback) {
                        callback();
                    }
                });

            return;
        }

        showPopup(setShowApiKeyPopup);

        // Always call callback even if no API key param exists
        if (callback) {
            callback();
        }
    }, [callback, enabled, setApiKeyAndApplication]);

    const handleCloseApiKeyPopup = () => {
        setShowApiKeyPopup(false);
        navigate(self.location.pathname || "/application");
    };

    const openApiKeyPopup = () => {
        setShowApiKeyPopup(true);
    };

    let name = getAppState().application?.name || "Insert API Key";
    let label = "Input an API key";
    let onClick = openApiKeyPopup;
    let icon = <IconKey />;

    if (isThereApiKeyParam()) {
        name = "";
        label = "";
        onClick = () => {
            /* no-op */
        };
        icon = <></>;
    }

    const apiKeyAction = {
        name: name,
        onClick: onClick,
        icon: icon,
        label: label,
    };

    return {
        showApiKeyPopup,
        handleCloseApiKeyPopup,
        apiKeyAction,
        openApiKeyPopup,
    };
};
